import { useState, useEffect, useRef } from 'react'
import { useAuth, GoogleButton } from '../auth/AuthContext'
import { useLang, SUPPORTED_LANGS } from '../i18n/LanguageContext'
import { startT } from '../i18n/startStrings'
import PlanCard, { SubscriberCard } from '../components/PlanCard'   // 共用：/start 與 /warroom 同一份價目

// ═══ QuanBrand 自助健檢漏斗 v2（BV01·2026-08-23）═══════════════════════════════
// 流程：Google 登入 → (紀錄留存) → **綁品牌** → 秒報分數 → 挑對手比一比 →
//       對照表 → 顧問 bot（資訊只來自他自己的報告）。免費一個品牌·更多/追蹤走付費。
//
// 🔴 2026-08-23 拿掉「選賽道」那一步（Allen 7/26 裁決·三賽道框架作廢）。
//    舊結構預設「客戶要先挑一種比法」，而新模型裡三個場域是**同時量的**，
//    客戶只需要說「我是誰」。官網 /quanbrand/ 早就是新模型，產品端漏了四週。
//    ⛔ 不是把 local 改名叫「品牌」—— 那樣 code 讀起來還是一個三選一的框架。
//    在地比分**沒有刪除，是降級**：它現在是 result → pick → compare 的
//    一次性診斷環節（Stage 2），不再是「持續追蹤的賽道」。
// 紀律：不承諾「N 秒」·缺口用損失框架·bot 只引報告（防幻覺）·Golden logo（白底）。

const LOGO = '/quanturing_logo_light.svg'   // Golden 主版（白/淺底用·brand-kit 紀律）

// 語言切換（驗收格 2：切了內容要真的變，不是只換 URL）。
// setLang 走 LanguageContext → 寫 localStorage → 整個 /start 一起換。
function LangSwitch({ dark = false }) {
  const { lang, setLang } = useLang()
  return (
    <div className="flex items-center gap-0.5">
      {SUPPORTED_LANGS.map(l => (
        <button key={l.id} onClick={() => setLang(l.id)}
          className={`text-[11px] px-2 py-1 rounded-full transition-colors ${lang === l.id
            ? 'bg-blue-600 text-white font-medium'
            : dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'}`}>
          {l.label}
        </button>
      ))}
    </div>
  )
}

const GRADE_STYLE = {
  A: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  B: 'text-blue-600 bg-blue-50 border-blue-200',
  C: 'text-amber-600 bg-amber-50 border-amber-200',
  D: 'text-orange-600 bg-orange-50 border-orange-200',
  E: 'text-rose-600 bg-rose-50 border-rose-200',
}

// api() 是模組層函式，拿不到 context。錯誤字用一個由 Start 每次 render 更新的
// 模組變數 —— 只有一個 /start 實例，這是最小的接縫，好過把 t 穿過每一個呼叫點。
let ERRWORD = '錯誤'

async function api(url, body) {
  const r = await fetch(url, body ? {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  } : {})
  const d = await r.json().catch(() => ({}))
  if (!r.ok) {
    const detail = d.detail
    if (detail && typeof detail === 'object') { const e = new Error(detail.message || ERRWORD); e.data = detail; throw e }
    throw new Error(detail || d.error || `${ERRWORD} ${r.status}`)
  }
  return d
}

// 🔴 付費者不該再看到「立即升級」。付錢之後畫面跟沒付錢一模一樣，
//    等於付錢沒有效果（Allen 2026-08-24 實測：pro 帳號在 compare 頁
//    底部仍被推銷）。四個推銷點統一走這裡。
function PlanOrStatus({ me, compact = false }) {
  if (me?.tier === 'paid') {
    return <SubscriberCard plan={me.plan} tracking={me.tracking_quota} compact={compact} />
  }
  return <PlanCard paidUrl={me?.paid_url} compact={compact} />
}

export default function Start() {
  const { user, signOut } = useAuth()
  const { lang } = useLang()
  const t = startT(lang)
  ERRWORD = t.err
  const [me, setMe] = useState(null)
  const [step, setStep] = useState('home')
  const [bizName, setBizName] = useState('')   // 一行公司名字
  const [siteUrl, setSiteUrl] = useState('')   // 一行網址
  const [picked, setPicked] = useState(null)   // 待確認的對象（按下「是」才會跑）
  const [rivalName, setRivalName] = useState('')  // 自己指定的對手
  const [rivalUrl, setRivalUrl] = useState('')
  const [cands, setCands] = useState(null)
  const [active, setActive] = useState(null)      // 當前品牌 { domain, score, top_actions, brand, competition }
  const [comp, setComp] = useState(null)          // 對手候選 / 對照表
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [limit, setLimit] = useState(null)        // 402 付費 gate 資料

  const loadMe = () => api('/start/api/me').then(setMe).catch(e => setErr(e.message))
  useEffect(() => { if (user) loadMe() }, [user])

  // 分頁標題也要換 —— start.html 的 <title> 寫死中文，
  // 而那是英文讀者在分頁列上唯一看得到的字。
  useEffect(() => { document.title = t.title }, [t])

  if (!user) return <Hero t={t} />

  const reset = () => { setStep('home'); setBizName(''); setSiteUrl(''); setPicked(null); setRivalName(''); setRivalUrl(''); setCands(null); setActive(null); setComp(null); setErr(''); setLimit(null); loadMe() }

  const host = (u) => {
    try { return new URL(u).hostname.replace(/^www\./, '').toLowerCase() } catch { return '' }
  }

  const normUrl = (raw) => {
    const v = raw.trim()
    if (!v) return ''
    return /^https?:\/\//i.test(v) ? v : 'https://' + v
  }

  const doLocate = async () => {
    const name = bizName.trim()
    const url = normUrl(siteUrl)
    if (!name && !url) { setErr(t.bind.needSomething); return }
    setErr(''); setBusy(true); setCands(null); setPicked(null)

    // 網址是**使用者親口給的身分**，比我們反查出來的更可信 → 永遠排第一。
    // 🔴 沒有 Google 商家檔案的品牌（我們自己就是）必須走得通：
    //    Places 搜「量識科技」回的是創量／汎銓／量測 —— 三家都不是他。
    let direct = url
      ? { place_id: '', name: name || host(url), website: url,
          _via: 'url', _gbp: name ? 'none' : 'unknown' }
      : null

    let found = []
    if (name) {
      try {
        const d = await api('/start/api/locate', { query: name })
        found = (d.candidates || []).map(c => ({ ...c, _via: 'place', _gbp: 'matched' }))
      } catch (e) { setErr(e.message) }
    }

    // 🔴 有商家檔案就**併進來一起評分**，不是排除掉。
    //    三場域裡 Google 地圖就是其中一格 —— 丟掉它等於少量三分之一。
    //    比對判準用**網域**：糕餅店、麵線攤這類本來就有商家檔案的，
    //    名稱一搜就中，網域一比就併得起來。
    if (direct) {
      const h = host(url)
      const hit = found.find(c => c.website && host(c.website) === h)
      if (hit) {
        direct = { ...hit, website: url, _via: 'url', _gbp: 'matched' }
        found = found.filter(c => c !== hit)
      }
    }
    setBusy(false)
    const all = [...(direct ? [direct] : []), ...found]
    setCands(all)
    // 只有一個對象時直接進確認 —— 從一個選項裡「選一個」不是選擇，是多一次點擊。
    if (all.length === 1) setPicked(all[0])
  }

  const runHealthcheck = async (brand) => {
    setErr(''); setBusy(true); setLimit(null)
    try {
      // lane 不再由使用者選 —— 後端欄位有預設值，這裡不送。
      // 它在後端是「用哪種方式找同業」的內部策略，不是客戶要回答的問題。
      const d = await api('/start/api/healthcheck', {
        place_id: brand?.place_id || '', url: brand?.website || '', brand: brand || {},
      })
      setActive({ ...d, brand }); setStep('result'); loadMe()
    } catch (e) {
      if (e.data?.paid_url) { setLimit(e.data); setStep('limit') } else setErr(e.message)
    } finally { setBusy(false) }
  }

  const openBrand = (b) => {
    setActive({ domain: b._id, score: b.score, top_actions: b.top_actions, brand: b.brand, competition: b.competition })
    setComp(b.competition || null)
    setStep('result')
  }

  const findCompetitors = async () => {
    setErr(''); setBusy(true)
    // 沒有周邊同業**不是錯誤** —— 照樣進 pick，讓他自己指定對手。
    // 舊版後端在這裡回 400「此品牌尚未綁定地圖商家」，整條路就斷在這裡。
    try { const d = await api('/start/api/competitors', { domain: active.domain }); setComp(d); setStep('pick') }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  // body 由呼叫端決定：地圖上的商家送 place_ids，自己指定的送 rivals。
  const doCompare = async (body) => {
    setErr(''); setBusy(true)
    try {
      const d = await api('/start/api/compare', { domain: active.domain, ...body })
      setComp(d); setStep('compare'); loadMe()
    } catch (e) {
      if (e.data?.paid_url) { setLimit(e.data); setStep('limit') } else setErr(e.message)
    } finally { setBusy(false) }
  }

  const runCompare = (placeId) => doCompare({ place_ids: [placeId] })

  // 🔴 自己指定的對手**不需要 Google 商家檔案** —— 比分是對網站打的。
  const runCompareManual = () => {
    const url = rivalUrl.trim()
    if (!url) { setErr(t.pick.rivalNeedUrl); return }
    return doCompare({ rivals: [{ name: rivalName.trim(), website: url }] })
  }

  return (
    <div className="min-h-dvh bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="fixed top-2 right-3 z-50 flex items-center gap-2 bg-white/90 backdrop-blur rounded-full border border-slate-200 shadow-sm pl-3 pr-1 py-1">
        <LangSwitch />
        <span className="text-xs text-slate-500 max-w-[160px] truncate">{user.email}</span>
        <button onClick={signOut} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600">{t.signOut}</button>
      </div>

      <div className="max-w-lg mx-auto px-4 py-10">
        <button onClick={reset}><img src={LOGO} alt="QuanTuring" className="h-8 mb-8" /></button>
        {err && <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}

        {step === 'home' && (
          <Home t={t} me={me} onStart={() => { setCands(null); setStep('bind') }} onOpen={openBrand} />
        )}

        {step === 'bind' && (
          <div>
            <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 mb-3">{t.bind.back}</button>
            <h2 className="text-lg font-bold text-slate-800 mb-1">{t.bind.title}</h2>
            <p className="text-sm text-slate-500 mb-4">{t.bind.sub}</p>
            <Fields t={t} />
            {/* 🔴 兩行，各自有標籤 —— 一個框要人自己判斷該打名稱還是網址，
                而判斷錯的代價是看到「找不到這間公司」。Allen 2026-08-23 指示。
                改字就清掉上一次結果：不清的話，使用者一邊打新的字，畫面一邊顯示
                上一題的答案，看起來像按了鈕沒反應。 */}
            <label className="block text-xs font-medium text-slate-600 mb-1">{t.bind.nameLabel}</label>
            <input value={bizName}
              onChange={e => { setBizName(e.target.value); setCands(null); setPicked(null) }}
              onKeyDown={e => e.key === 'Enter' && doLocate()}
              placeholder={t.bind.namePlaceholder}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-blue-400" />
            <label className="block text-xs font-medium text-slate-600 mt-3 mb-1">{t.bind.urlLabel}</label>
            <input value={siteUrl}
              onChange={e => { setSiteUrl(e.target.value); setCands(null); setPicked(null) }}
              onKeyDown={e => e.key === 'Enter' && doLocate()}
              placeholder={t.bind.urlPlaceholder}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-blue-400" />
            <p className="text-[11px] text-slate-400 mt-1.5">{t.bind.urlHelp}</p>
            <button onClick={doLocate} disabled={busy}
              className="mt-3 w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {busy ? t.bind.searching : t.bind.search}
            </button>

            {/* 確認卡：按下「是」之前不會跑任何東西 */}
            {picked && <ConfirmCard t={t} c={picked} busy={busy}
              onYes={() => runHealthcheck(picked)}
              onNo={() => { setPicked(null); setCands(null) }} />}

            {!picked && cands && cands.length === 0 && (
              <p className="text-sm text-slate-500 mt-4 leading-relaxed">{t.bind.noResult}</p>
            )}
            {!picked && cands && cands.length > 1 && (
              <div className="mt-5">
                <p className="text-xs text-slate-500 mb-2">{t.confirm.title}</p>
                <div className="space-y-2">
                  {/* 直接用網址的那筆沒有 place_id（空字串）—— key 要有東西可用，
                      否則同一頁多筆會共用空 key，React 會把它們當同一個。 */}
                  {cands.map(c => <PlaceRow key={c.place_id || c.website} c={c}
                    onClick={() => setPicked(c)} disabled={busy} />)}
                </div>
              </div>
            )}
            {busy && <p className="text-sm text-slate-400 mt-4">{t.bind.running}</p>}
          </div>
        )}

        {step === 'result' && active && (
          <Result t={t} active={active} comp={comp} busy={busy}
            onCompete={findCompetitors} onChat={() => setStep('chat')} onBack={reset} />
        )}

        {step === 'pick' && comp && (
          <div>
            <button onClick={() => setStep('result')} className="text-xs text-slate-400 hover:text-slate-600 mb-3">{t.pick.back}</button>
            <h2 className="text-lg font-bold text-slate-800 mb-1">{t.pick.title}</h2>
            <p className="text-sm text-slate-500 mb-4">{t.pick.sub}</p>
            {(comp.competitors || []).length > 0 ? (
              <>
                <div className="text-xs font-medium text-slate-600 mb-2">{t.pick.nearbyTitle}</div>
                <div className="space-y-2">
                  {comp.competitors.map(c => <PlaceRow key={c.place_id} c={c}
                    onClick={() => runCompare(c.place_id)} disabled={busy} />)}
                </div>
              </>
            ) : (
              /* 🔴 沒有周邊同業不是死路。「周邊同業」本來就是**在地賽道**的找法
                 （掃半徑 800 公尺），BV01 把三賽道作廢改成品牌優先之後，
                 它不該是唯一的路 —— 緯創、Appier 不在任何人的 800 公尺內，
                 但他們是真的對手。 */
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
                {t.pick.noNearby}<br />
                <span className="text-slate-500">{t.pick.noNearbyOk}</span>
              </div>
            )}

            <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50/40 p-4">
              <div className="text-sm font-bold text-slate-800">{t.pick.manualTitle}</div>
              <div className="text-[11px] text-slate-500 mb-3">{t.pick.manualSub}</div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t.pick.rivalName}</label>
              <input value={rivalName} onChange={e => setRivalName(e.target.value)}
                placeholder={t.pick.rivalNamePlaceholder}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-blue-400" />
              <label className="block text-xs font-medium text-slate-600 mt-3 mb-1">{t.pick.rivalUrl}</label>
              <input value={rivalUrl} onChange={e => setRivalUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runCompareManual()}
                placeholder={t.pick.rivalUrlPlaceholder}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-blue-400" />
              <button onClick={runCompareManual} disabled={busy || !rivalUrl.trim()}
                className="mt-3 w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
                {busy ? t.pick.analyzing : t.pick.compareThis}
              </button>
            </div>
            {busy && <p className="text-sm text-slate-400 mt-4">{t.pick.analyzing}</p>}
          </div>
        )}

        {step === 'compare' && comp?.rows && (
          <Compare t={t} comp={comp} me={me} onChat={() => setStep('chat')} onBack={() => setStep('result')} />
        )}

        {step === 'chat' && active && (
          <ChatPanel t={t} lang={lang} domain={active.domain} me={me} onBack={() => setStep(comp?.rows ? 'compare' : 'result')} />
        )}

        {step === 'limit' && limit && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🔒</div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">{t.limit.title}</h2>
            <p className="text-sm text-slate-500 mb-5">{limit.message}</p>
            <PlanOrStatus me={me} />
            <div><button onClick={reset} className="text-sm text-slate-500 mt-4 hover:text-slate-700">{t.limit.back}</button></div>
          </div>
        )}
      </div>
    </div>
  )
}

// 三場域：**說明，不是選項**（BV01）。客戶不必先挑一種比法 ——
// 三個是同時量的，他只要說「我是誰」。
// 文字在 startStrings 裡，逐字取自已上線的 /quanbrand/。
function Fields({ t }) {
  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-white/70 p-4">
      <div className="text-xs font-bold text-slate-700 mb-2">{t.fields.title}</div>
      <ul className="space-y-2">
        {t.fields.items.map(([icon, name, desc]) => (
          <li key={name} className="flex gap-2.5">
            <span className="text-base leading-none mt-0.5">{icon}</span>
            <div>
              <div className="text-sm font-medium text-slate-800">{name}</div>
              <div className="text-[11px] text-slate-500 leading-relaxed">{desc}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Hero({ t }) {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-md w-full mx-4 bg-white rounded-2xl shadow-lg border border-slate-100 p-8 text-center">
        {/* 語言切換在登入前就要在 —— 從英文頁進來卻想看中文的人，
            在按下 Google 登入之前就得有辦法切回去。 */}
        <div className="flex justify-end mb-2"><LangSwitch /></div>
        <img src={LOGO} alt="QuanTuring" className="h-9 mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-slate-800 leading-snug mb-3 whitespace-pre-line">
          {t.hero.title}
        </h1>
        <p className="text-sm text-slate-500 mb-6">{t.hero.sub}</p>
        <div className="flex justify-center"><GoogleButton loadingLabel={t.hero.signinLoading} locale={t.hero.gisLocale}
          switchLabel={t.hero.switchAccount} fallbackLabel={t.hero.signinFallback} /></div>
        <p className="text-[11px] text-slate-400 mt-5">{t.hero.loginNote}</p>
        <details className="mt-4 text-left">
          <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-slate-600 text-center">
            {t.hero.privacySummary}
          </summary>
          <div className="text-[11px] text-slate-500 leading-relaxed mt-2 bg-slate-50 rounded-lg p-3 space-y-1.5">
            {t.hero.privacy.map(([head, body]) => (
              <p key={head}><b>{head}</b>{body}</p>
            ))}
          </div>
        </details>
        <p className="text-[11px] text-slate-300 mt-8">© 2026 QuanTuring Inc.</p>
      </div>
    </div>
  )
}

function Home({ t, me, onStart, onOpen }) {
  const brands = me?.brands || []
  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800 mb-1">{t.home.title}</h2>
      <p className="text-sm text-slate-500 mb-3">
        {brands.length ? t.home.subHas : t.home.subEmpty}
      </p>
      {me?.chat_quota && (
        <div className="flex gap-2 mb-5 text-[11px]">
          <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
            {t.home.chatQuota} {Math.max(0, me.chat_quota.limit - me.chat_quota.used)}/{me.chat_quota.limit}
            {me.chat_quota.scope === 'day' ? t.home.today : ''}
          </span>
          {/* 🔴 付費的 compare 是冷卻期制，limit=null —— 不能拿去做算術，
              會顯示 NaN/null（ops 卡上實際露出過「1/null」）。 */}
          {me.compare_quota && me.compare_quota.limit != null && (
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
              {t.home.compareQuota} {Math.max(0, me.compare_quota.limit - me.compare_quota.used)}/{me.compare_quota.limit}
              {me.compare_quota.scope === 'month' ? t.home.month : ''}
            </span>
          )}
          {me.tracking_quota && me.tier === 'paid' && (
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
              {t.home.trackingQuota} {me.tracking_quota.used}/{me.tracking_quota.limit}
            </span>
          )}
          {me.tier === 'paid' && <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">{t.home.subscribed}</span>}
        </div>
      )}
      <div className="space-y-3 mb-5">
        {brands.map(b => (
          <button key={b._id} onClick={() => onOpen(b)}
            className="w-full text-left p-4 rounded-2xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-medium text-slate-800 text-sm truncate">{b.brand?.name || b._id}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {b.probed_at ? `${t.home.probedAt} ${String(b.probed_at).slice(0, 10)}` : ''}
                  {b.competition ? t.home.compared : ''}
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-xl border text-center flex-shrink-0 ml-3 ${GRADE_STYLE[b.score?.grade] || GRADE_STYLE.E}`}>
                <div className="text-lg font-bold tabular-nums leading-none">{b.score?.total ?? '—'}</div>
                <div className="text-[10px] opacity-70">{b.score?.grade}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      {me?.can_add_brand ? (
        <button onClick={onStart} className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
          {brands.length ? t.home.addMore : t.home.addFirst}
        </button>
      ) : <PlanOrStatus me={me} />}
    </div>
  )
}

// 確認卡（Allen 2026-08-23）：**按下「是」之前不會跑任何東西。**
// 舊版是點候選就直接開跑 —— 使用者沒有機會確認我們找到的是不是他。
function ConfirmCard({ t, c, busy, onYes, onNo }) {
  const viaUrl = c._via === 'url'
  return (
    <div className="mt-5 rounded-2xl border-2 border-blue-400 bg-blue-50/40 p-4">
      <div className="text-sm font-bold text-slate-800 mb-1">{t.confirm.title}</div>
      <div className="text-[11px] text-slate-500 mb-3">{t.confirm.sub}</div>
      <div className="rounded-xl bg-white border border-slate-200 p-3">
        <div className="font-medium text-slate-800 text-sm">{c.name}</div>
        {c.address && <div className="text-xs text-slate-500 mt-0.5">{c.address}</div>}
        {c.rating != null && (
          <div className="text-xs text-slate-500 mt-0.5">⭐{c.rating}（{c.review_count}）</div>
        )}
        {c.website
          ? <div className="text-[11px] text-blue-600 mt-1 break-all">{c.website}</div>
          : <div className="text-[11px] text-amber-700 mt-1">{t.confirm.noSite}</div>}
        <div className="text-[11px] text-slate-400 mt-2">
          {viaUrl ? t.confirm.viaUrl : t.confirm.viaPlace}
        </div>
        {/* 🔴 三態，而且**分得出「找過沒有」與「根本沒去找」**。
            舊版對「使用者給了網址」一律寫「沒有比對到商家檔案」——
            但只給網址時我們根本沒搜過，那是在報告一個沒跑過的搜尋的結果。 */}
        {c._gbp === 'matched' && (
          <div className="text-[11px] text-emerald-700 mt-1">{t.confirm.gbpMatched}</div>
        )}
        {c._gbp === 'none' && (
          <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{t.confirm.gbpNone}</div>
        )}
        {c._gbp === 'unknown' && (
          <div className="text-[11px] text-amber-700 mt-1 leading-relaxed">{t.confirm.gbpUnknown}</div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onYes} disabled={busy || !c.website}
          className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
          {busy ? t.bind.running : t.confirm.yes}
        </button>
        <button onClick={onNo} disabled={busy}
          className="px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-600 text-sm hover:border-slate-400">
          {t.confirm.no}
        </button>
      </div>
    </div>
  )
}

function PlaceRow({ c, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="w-full text-left p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-sm disabled:opacity-50">
      <div className="font-medium text-slate-800 text-sm">{c.name}</div>
      <div className="text-xs text-slate-500 mt-0.5">
        {c.primary_type_label && <span>{c.primary_type_label} · </span>}
        {c.rating != null && <span>⭐{c.rating}（{c.review_count}）· </span>}
        {c.address}
      </div>
      {c.website && <div className="text-[11px] text-blue-500 mt-0.5 truncate">{c.website}</div>}
    </button>
  )
}

function Result({ t, active, comp, busy, onCompete, onChat, onBack }) {
  const sc = active.score || {}
  const top = active.top_actions || []
  const hasCompare = !!comp?.rows
  return (
    <div>
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-slate-600 mb-3">{t.result.back}</button>
      <div className={`rounded-2xl border p-6 text-center mb-5 ${GRADE_STYLE[sc.grade] || GRADE_STYLE.E}`}>
        <div className="text-xs font-medium uppercase tracking-wider opacity-70 mb-1">
          {active.brand?.name || t.result.brandFallback} · {t.result.scoreLabel}
        </div>
        <div className="text-5xl font-black tabular-nums">{sc.total ?? '—'}<span className="text-2xl opacity-60">/100</span></div>
        <div className="text-lg font-bold mt-1">{t.result.grade} {sc.grade}</div>
        <div className="text-xs opacity-70 mt-2">{t.result.tally(sc.passes ?? 0, sc.warns ?? 0, sc.fails ?? 0)}</div>
      </div>

      {top.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-bold text-slate-700 mb-3">{t.result.topTitle}</h3>
          <div className="space-y-3">
            {top.map(a => (
              <div key={a.rank} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">{a.rank}</span>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800 text-sm">{a.title}</div>
                    {/* ⚠️ a.title / a.loss / a.evidence 由後端 qbplatform/audit.py 產生，
                        目前**只有中文**。標籤三語了、內容還沒 —— 已回報 gcp-pro，不自行決定。 */}
                    {a.loss && <div className="text-xs text-rose-600 mt-1">📉 {t.result.loss}{a.loss}</div>}
                    {a.evidence && <div className="text-[11px] text-slate-400 mt-1">{t.result.evidence}{a.evidence}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <button onClick={onCompete} disabled={busy}
          className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {busy ? t.result.findingRivals : hasCompare ? t.result.findRivalsAgain : t.result.findRivals}
        </button>
        <button onClick={onChat}
          className="w-full py-3 rounded-xl bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:border-blue-400">
          {t.result.chatCta}
        </button>
      </div>
    </div>
  )
}

function Compare({ t, comp, me, onChat, onBack }) {
  const rows = [...(comp.rows || [])].sort((a, b) => (b.audit_score ?? -1) - (a.audit_score ?? -1))
  const mine = comp.rows.find(r => r.is_subject) || {}
  return (
    <div>
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-slate-600 mb-3">{t.pick.back}</button>
      <h2 className="text-lg font-bold text-slate-800 mb-1">{t.compare.title}</h2>
      <p className="text-sm text-slate-500 mb-4">
        {t.compare.sub}
        {comp.subject_rank && <> {t.compare.rank(comp.subject_rank, comp.total)}</>}
      </p>
      <div className="space-y-2 mb-5">
        {rows.map(r => (
          <div key={r.place_id || r.name}
            className={`p-3 rounded-xl border flex items-center gap-3 ${r.is_subject ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200'}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">
                {r.name}{r.is_subject && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-600 text-white">{t.compare.you}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {/* 🔴 三態：有評價／確實沒有評價／**我們沒去比對商家檔案**。
                    第三種以前也印「無評價」—— 那是替對方宣稱一件我們沒查證的事。 */}
                {r.rating != null
                  ? `⭐${r.rating}（${r.review_count}）`
                  : (r.gbp_checked === false ? t.compare.gbpNotChecked : t.compare.noRating)}
                {!r.has_website && t.compare.noWebsite}
              </div>
            </div>
            <div className={`px-2.5 py-1.5 rounded-lg border text-center flex-shrink-0 ${GRADE_STYLE[r.audit_grade] || 'text-slate-400 bg-slate-50 border-slate-200'}`}>
              <div className="text-base font-bold tabular-nums leading-none">{r.audit_score ?? '—'}</div>
              <div className="text-[10px] opacity-70">{r.audit_grade || t.compare.noSite}</div>
            </div>
          </div>
        ))}
      </div>
      {comp.competitor_avg_score != null && (
        <p className="text-xs text-slate-500 mb-5">
          {t.compare.avg(comp.competitor_avg_score, mine.audit_score ?? '—')}
          {t.compare.avgTail}
        </p>
      )}
      {/* ═══ 逐項差異 ═══════════════════════════════════════════
          雙方的 16 項結果本來就算出來了，舊版只留三個數字就丟掉其餘。
          「85 對 83」不會讓任何人知道要做什麼；「他有你沒有的是這幾項」會。 */}
      {(comp.diff || []).map(d => (
        <div key={d.place_id} className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold text-slate-800 mb-1">{t.compare.diffTitle}</div>
          <div className="text-[11px] text-slate-400 mb-3">{d.name}</div>
          {d.they_win.length === 0 && d.you_win.length === 0 && (
            <p className="text-xs text-slate-500 leading-relaxed">{t.compare.noDiff}</p>
          )}
          {d.they_win.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-rose-700 mb-1.5">
                📉 {t.compare.theyWin(d.they_win.length)}
              </div>
              <ul className="space-y-1">
                {d.they_win.map(x => (
                  <li key={x} className="text-xs text-slate-700 flex gap-1.5">
                    <span className="text-rose-500">✗</span>{x}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {d.you_win.length > 0 && (
            <div>
              <div className="text-xs font-medium text-emerald-700 mb-1.5">
                ✓ {t.compare.youWin(d.you_win.length)}
              </div>
              <ul className="space-y-1">
                {d.you_win.map(x => (
                  <li key={x} className="text-xs text-slate-600 flex gap-1.5">
                    <span className="text-emerald-500">✓</span>{x}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}

      <button onClick={onChat} className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
        {t.compare.chatCta}
      </button>
      <div className="mt-6">
        <p className="text-xs text-slate-500 mb-2 text-center">{t.compare.snapshot}</p>
        <PlanOrStatus me={me} compact />
      </div>
    </div>
  )
}

function ChatPanel({ t, lang, domain, me, onBack }) {
  const GREETING = t.chat.greeting
  const [msgs, setMsgs] = useState([{ role: 'bot', text: GREETING }])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [leadSent, setLeadSent] = useState(false)
  const endRef = useRef(null)

  // 對話存在雲端——重新登入也接得回來
  useEffect(() => {
    api('/start/api/chat-history', { domain }).then(d => {
      const turns = d.turns || []
      if (!turns.length) return
      const restored = [{ role: 'bot', text: GREETING }]
      turns.forEach(t => {
        if (t.user) restored.push({ role: 'user', text: t.user })
        if (t.assistant) restored.push({ role: 'bot', text: t.assistant })
      })
      setMsgs(restored)
    }).catch(() => {})
  }, [domain])

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [msgs, busy])

  const send = async () => {
    const text = input.trim()
    if (!text || busy) return
    setInput(''); setBusy(true)
    const next = [...msgs, { role: 'user', text }]
    setMsgs(next)
    try {
      const history = next.slice(-9, -1).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }))
      // 🔴 lang 一起送：UI 換成英文而顧問仍用中文回答，
      //    正是這一波要修的那個「走到第二步才發現不是」。
      const d = await api('/start/api/chat', { domain, message: text, history, lang })
      setMsgs(m => [...m, { role: 'bot', text: d.answer }])
      if (d.lead_sent) setLeadSent(true)
    } catch (e) {
      setMsgs(m => [...m, { role: 'bot', text: t.chat.failed(e.message) }])
    } finally { setBusy(false) }
  }

  return (
    <div>
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-slate-600 mb-3">{t.back}</button>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="text-sm font-bold text-slate-800">{t.chat.title}</div>
          <div className="text-[11px] text-slate-400">{t.chat.sub}</div>
        </div>
        <div className="p-4 space-y-3 max-h-[52vh] overflow-y-auto">
          {msgs.map((m, i) => (
            <div key={i} className={`text-sm px-3 py-2 rounded-xl whitespace-pre-wrap ${m.role === 'user'
              ? 'bg-blue-600 text-white ml-8' : 'bg-slate-100 text-slate-700 mr-8'}`}>{m.text}</div>
          ))}
          {busy && <div className="text-sm text-slate-400 mr-8">{t.chat.thinking}</div>}
          {leadSent && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mr-8">
              {t.chat.leadSent}
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-slate-100 flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
            placeholder={t.chat.placeholder} disabled={busy}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-blue-400" />
          <button onClick={send} disabled={busy || !input.trim()}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">{t.chat.send}</button>
        </div>
      </div>
      <div className="mt-6"><PlanOrStatus me={me} compact /></div>
    </div>
  )
}
